import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { products, Product } from '@/mocks/products';
import ProductQuickView from '@/components/feature/ProductQuickView';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const categories = [
  { id: 'all', label: 'All Products' },
  { id: 'Makhana', label: 'Makhana' },
  { id: 'Snacks', label: 'Snacks' },
  { id: 'Spices', label: 'Spices' },
  { id: 'Tea', label: 'Tea' },
  { id: 'Pickles', label: 'Pickles' },
  { id: 'Water', label: 'Water' },
];

const features = [
  { icon: 'ri-shield-check-line', title: 'Quality Assured', desc: 'FSSAI Certified' },
  { icon: 'ri-truck-line', title: 'Free Shipping', desc: 'Pan-India Delivery' },
  { icon: 'ri-refresh-line', title: 'Easy Returns', desc: '7-day return policy' },
  { icon: 'ri-customer-service-line', title: '24/7 Support', desc: 'Always here to help' },
];

export default function ProductsPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const schemaData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/products#webpage`,
          url: `${SITE_URL}/products`,
          name: 'Our Products | Faasley Foods — Premium Indian Packaged Food',
          description: 'Explore Faasley Foods\' premium range of authentic Indian food products — Makhana snacks, spices, tea, pickles, and drinking water. FSSAI certified quality.',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Products', item: `${SITE_URL}/products` },
            ],
          },
        },
        {
          '@type': 'ItemList',
          name: 'Faasley Foods Product Range',
          description: 'Premium Indian packaged food products by Faasley Foods',
          url: `${SITE_URL}/products`,
          numberOfItems: products.length,
          itemListElement: products.map((p, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: p.name,
            url: `${SITE_URL}/products/${p.id}`,
          })),
        },
        {
          '@type': 'Store',
          name: 'Faasley Foods',
          url: `${SITE_URL}/products`,
          description: 'Premium Indian packaged food products — Makhana, Spices, Tea, Pickles, Water',
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Faasley Foods Product Catalog',
            itemListElement: [
              { '@type': 'OfferCatalog', name: 'Makhana Snacks' },
              { '@type': 'OfferCatalog', name: 'Indian Spices' },
              { '@type': 'OfferCatalog', name: 'Premium Tea' },
              { '@type': 'OfferCatalog', name: 'Pickles' },
              { '@type': 'OfferCatalog', name: 'Drinking Water' },
            ],
          },
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-products';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
    document.title = 'Our Products | Faasley Foods — Premium Indian Packaged Food';
    return () => {
      const el = document.getElementById('schema-products');
      if (el) el.remove();
    };
  }, []);

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter((p) => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ── Page Hero ── */}
      <div className="relative pt-[240px] md:pt-[260px]">
        <div className="h-[280px] md:h-[380px] relative overflow-hidden bg-stone-950">
          <img
            src="https://storage.readdy-site.link/project_files/c44a33b9-73a7-4f29-8c86-80a04dae53c1/b85f1443-a55f-42e9-8b00-a5ef5af15504_Products.jpg?v=22d4c66c156c36d8d9e31225781697f9"
            alt="Our Products"
            className="w-full h-full object-cover object-center opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-950/50 to-stone-950/30"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
              <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.2em] mb-4">Faasley Foods</p>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                Our Products
              </h1>
              <p className="text-white/60 text-base md:text-lg max-w-xl">
                Discover our premium range of authentic Indian food products, crafted with care and tradition.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Products Section ── */}
      <section className="py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-5 py-2 rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category.id
                    ? 'bg-stone-900 text-white'
                    : 'bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-900'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="group bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-amber-200 transition-all duration-300 cursor-pointer"
                onClick={() => navigate(`/products/${product.id}`)}
              >
                {/* Image */}
                <div className="relative aspect-square overflow-hidden bg-stone-50">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {product.badge && (
                    <span className="absolute top-2.5 left-2.5 bg-amber-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                      {product.badge}
                    </span>
                  )}
                  <button
                    className="absolute top-2.5 right-2.5 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-stone-400 hover:text-amber-500 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); setQuickViewProduct(product); }}
                  >
                    <i className="ri-eye-line text-sm"></i>
                  </button>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                    <span className="bg-white text-stone-900 font-semibold px-5 py-2 rounded-full text-xs opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 whitespace-nowrap">
                      View Details
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-3.5">
                  <p className="text-amber-600 text-[10px] font-bold uppercase tracking-wider mb-0.5">{product.brand}</p>
                  <h3 className="font-semibold text-stone-900 text-sm leading-snug line-clamp-1 mb-1">{product.name}</h3>
                  <p className="text-stone-400 text-xs line-clamp-1 mb-3">{product.description}</p>
                  <Link
                    to={`/products/${product.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full bg-stone-900 hover:bg-amber-600 text-white text-xs font-semibold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-right-line"></i>
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-stone-400 text-2xl"></i>
              </div>
              <h3 className="text-stone-900 font-semibold text-lg mb-2">No products found</h3>
              <p className="text-stone-400 text-sm">Try selecting a different category</p>
            </div>
          )}
        </div>
      </section>

      {/* ── Trust Features ── */}
      <section className="py-14 bg-stone-50 border-t border-stone-100">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-2xl mb-4">
                  <i className={`${feature.icon} text-amber-600 text-xl`}></i>
                </div>
                <h4 className="font-semibold text-stone-900 text-sm mb-1">{feature.title}</h4>
                <p className="text-stone-400 text-xs">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <ProductQuickView
        product={quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
      />
    </div>
  );
}
